/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';

@Component({
  selector: 'list-column-with-tooltip',
  templateUrl: './list-column-with-tooltip.component.html',
  styleUrls: ['./list-column-with-tooltip.component.scss'],
  standalone: false,
})

///designed to be used on lists where a column has a tooltip
export class ListColumnWithTooltipComponent implements AfterViewInit, OnChanges {
  //An optional input for you to include if the tooltip is different from the value displayed in the row.
  @Input() tooltipOverride: string;
  //This input is required to implement the component, the value is displayed in the table row.
  //It will also be displayed in the tooltip if not overridden by an input tooltipOverride.
  @Input() rowValue: string;
  //An optional input for you to include if a router link has any access restrictions, this is true by default.
  @Input() canAccessLink: boolean = true;
  //An optional input for you to included if the field also has a router link.
  @Input() route: any;
  //An optional input for you to include any query parameters with the router link.
  @Input() params: any;
  //An optional input for you to included if links should open in the same tab, this is true by default.
  @Input() openInNewTab: boolean = false;
  //An optional input for you to included if the text should have a click method, this is false by default.
  @Input() hasClickMethod: boolean = false;
  //Outputs that the click method was called, this is used to trigger the clickMethod in the parent component.
  @Output() clickMethodCalled: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('tooltip') tooltipElement?: ElementRef<HTMLElement>;

  tooltipDisabled = true;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngAfterViewInit() {
    this.updateTooltipDisabled();
    this.changeDetectorRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.rowValue || changes.tooltipOverride) {
      this.updateTooltipDisabled();
    }
  }

  //Most implementations of tooltips show the same value displayed in the row in the tooltip
  //This checks for an override value and sets the tooltip appropriately
  get tooltipDisplay() {
    return this.hasTooltipOverride ? this.tooltipOverride : this.rowValue;
  }

  //Checks for an input Tooltip Override
  get hasTooltipOverride() {
    return this.tooltipOverride && this.tooltipOverride.length !== 0;
  }

  //Checks for an input route
  get hasRouting() {
    return this.route && this.route.length !== 0;
  }

  //Checks if there is a route and if the user can access it to show/hide the link
  get showLink() {
    return this.hasRouting && this.canAccessLink;
  }

  //disables the tooltip based on the width and if there is not a override for the tooltip
  //the assumption here is that if the tooltip is being overridden to show a different value it would always want to be available
  updateTooltipDisabled() {
    const element = this.tooltipElement?.nativeElement;

    if (!element) {
      this.tooltipDisabled = true;
      return;
    }

    this.tooltipDisabled = !this.hasTooltipOverride && element.scrollWidth <= element.clientWidth;
  }

  clickMethod() {
    this.clickMethodCalled.emit();
  }
}
